import { ref } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

import { PermissionsGroupsEnumType } from './features/auth/types'

import ForgotPassword from '@/(routes)/(auth)/forgot-password/page.vue'
import Invitation from '@/(routes)/(auth)/invitation/[token]/page.vue'
import Login from '@/(routes)/(auth)/login/page.vue'
import CreatePlatform from '@/(routes)/(auth)/setup/page.vue'
import AdminDashboard from '@/(routes)/(protected)/(app)/admin/dashboard/page.vue'
import DownloadDelivrable from '@/(routes)/(protected)/(app)/admin/download-deliverable/page.vue'
import AdminGroups from '@/(routes)/(protected)/(app)/admin/groups/page.vue'
import AdminProgramSettings from '@/(routes)/(protected)/(app)/admin/program-settings/page.vue'
import AdminReminders from '@/(routes)/(protected)/(app)/admin/reminders/page.vue'
import AdminSessions from '@/(routes)/(protected)/(app)/admin/sessions/page.vue'
import AdminSettings from '@/(routes)/(protected)/(app)/admin/settings/page.vue'
import AdminPlatformSetup from '@/(routes)/(protected)/(app)/admin/setup/page.vue'
import AdminUsers from '@/(routes)/(protected)/(app)/admin/users/page.vue'
import Chat from '@/(routes)/(protected)/(app)/chat/page.vue'
import Dashboard from '@/(routes)/(protected)/(app)/dashboard/page.vue'
import IndexProfile from '@/(routes)/(protected)/(app)/profile/page.vue'
import Sessions from '@/(routes)/(protected)/(app)/sessions/page.vue'
import Redirect from '@/(routes)/(protected)/redirect/page.vue'
import LegalCGU from '@/(routes)/cgu/page.vue'
import LegalPrivacy from '@/(routes)/privacy/page.vue'
import { captureReturnTo } from '@/features/auth/hooks/use-return-to.hook'
import authStore from '@/features/auth/stores/auth/auth.store'
import dayjsClient from '@/lib/dayjs'
import { getGlobalI18nInstance, getLocaleFromLocalStorage, loadLocaleMessages, switchI18nLanguage } from '@/lib/i18n'
import { PosthogClient } from '@/lib/posthog'
import routeStore from '@/store/route/route.store'
import { setRouteTitleKey } from '@/utils/misc/document-title'

const routes = [
  {
    path: '/',
    name: 'Login',
    component: Login,
    meta: {
      titleKey: 'ui.pageTitle.login',
      public: true,
    },
  },
  {
    path: '/setup',
    name: 'CreatePlatform',
    component: CreatePlatform,
    meta: {
      titleKey: 'ui.pageTitle.createPlatform',
      public: true,
    },
  },
  {
    path: '/redirect',
    name: 'Redirect',
    component: Redirect,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/mot-de-passe-oublie/:token?',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: {
      titleKey: 'ui.pageTitle.forgotPassword',
      public: true,
    },
  },
  {
    path: '/invitation/:token',
    name: 'Invitation',
    component: Invitation,
    meta: {
      titleKey: 'ui.pageTitle.invitation',
      public: true,
    },
  },
  {
    path: '/cgu',
    name: 'LegalCGU',
    component: LegalCGU,
    meta: {
      titleKey: 'ui.pageTitle.cgu',
      public: true,
    },
  },
  {
    path: '/privacy',
    name: 'LegalPrivacy',
    component: LegalPrivacy,
    meta: {
      titleKey: 'ui.pageTitle.privacy',
      public: true,
    },
  },
  {
    path: '/chat/:roomId?',
    name: 'Chat',
    component: Chat,
    meta: {
      titleKey: 'ui.pageTitle.chat',
      requiresAuth: true,
      rejectGuest: true,
      rejectAdmin: true,
    },
  },
  {
    path: '/dashboard',
    name: 'IndexMentoringGroup',
    component: Dashboard,
    meta: {
      titleKey: 'ui.pageTitle.dashboard',
      requiresAuth: true,
      rejectGuest: true,
      rejectAdmin: true,
    },
  },
  {
    path: '/profile',
    name: 'NewIndexProfile',
    component: IndexProfile,
    meta: {
      titleKey: 'ui.pageTitle.profile',
      requiresAuth: true,
      rejectGuest: true,
    },
  },
  {
    path: '/sessions',
    name: 'SessionIndex',
    component: Sessions,
    meta: {
      titleKey: 'ui.pageTitle.sessions',
      requiresAuth: true,
      rejectGuest: true,
      rejectAdmin: true,
    },
  },
  {
    path: '/SAML',
    name: 'SAML',
    beforeEnter() {
      if (import.meta.env.VITE_API_URL) {
        location.href = `${import.meta.env.VITE_API_URL}/v1/auth/saml`
      }
    },
  },
  {
    path: '/admin/program-settings/edit',
    name: 'AdminEditProgramSettings',
    component: AdminProgramSettings,
    meta: {
      titleKey: 'ui.pageTitle.adminProgramSettings',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/program-settings/new',
    name: 'AdminNewProgramSettings',
    component: AdminProgramSettings,
    meta: {
      titleKey: 'ui.pageTitle.adminNewProgram',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/reminders',
    name: 'AdminReminders',
    component: AdminReminders,
    meta: {
      titleKey: 'ui.pageTitle.adminReminders',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/sessions',
    name: 'AdminSessions',
    component: AdminSessions,
    meta: {
      titleKey: 'ui.pageTitle.adminSessions',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/groups',
    name: 'AdminGroups',
    component: AdminGroups,
    meta: {
      titleKey: 'ui.pageTitle.adminGroups',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: {
      titleKey: 'ui.pageTitle.adminDashboard',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsers,
    meta: {
      titleKey: 'ui.pageTitle.adminUsers',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/settings',
    name: 'AdminSettings',
    component: AdminSettings,
    meta: {
      titleKey: 'ui.pageTitle.adminSettings',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/setup',
    name: 'AdminPlatformSetup',
    component: AdminPlatformSetup,
    meta: {
      titleKey: 'ui.pageTitle.adminSetup',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
  {
    path: '/admin/session/:sessionId/user/:userId/deliverable/:deliverableId',
    name: 'DownloadDeliverable',
    component: DownloadDelivrable,
    meta: {
      titleKey: 'ui.pageTitle.downloadDeliverable',
      requiresAuth: true,
      rejectMentor: true,
      rejectMentee: true,
      rejectGuest: true,
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 }
  },
})

export const currentRouteRef = ref(router.currentRoute)

router.beforeEach(async (to, from, next) => {
  const paramsLocal = getLocaleFromLocalStorage()
  const userLang = authStore.userLanguage?.toLowerCase()
  const activeUserPermissionGroup = authStore.userActivePermissionGroup
  const i18nInstance = getGlobalI18nInstance()

  if (i18nInstance) {
    await loadLocaleMessages(i18nInstance, paramsLocal)

    if (userLang) {
      switchI18nLanguage(i18nInstance, userLang)
      dayjsClient.locale(userLang)
    } else if (paramsLocal !== i18nInstance.global.locale.value) {
      switchI18nLanguage(i18nInstance, paramsLocal)
      dayjsClient.locale(paramsLocal)
    } else {
      const defaultLang = 'fr'
      switchI18nLanguage(i18nInstance, defaultLang)
      dayjsClient.locale(defaultLang)
    }
  }

  // Après chargement i18n : le sujet est disponible, on pose le titre tout de
  // suite (WCAG 2.4.2). afterEach le reprendra si cette navigation redirige.
  setRouteTitleKey(to.meta?.titleKey)

  // if the user is admin and actually not allowed to see this route, redirect this one to the redirect page
  if (to.meta.rejectAdmin && activeUserPermissionGroup === PermissionsGroupsEnumType.ADMIN) {
    return next({ name: 'Redirect' })
  }

  // if the user is user and actually not allowed to see an admin route, redirect this one to the redirect page
  if ((to.meta.rejectMentee || to.meta.rejectMentor) && activeUserPermissionGroup === PermissionsGroupsEnumType.USER) {
    return next({ name: 'Redirect' })
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!authStore.authToken) {
      // préserve le deep-link (path + query) pour le restituer après login
      captureReturnTo(to.fullPath)
      const loginpath = window.location.pathname // enregistre la page de provenance
      return next({ name: 'Login', query: { from: loginpath } })
    }
  }
  if (to.matched.some((record) => record.meta.public)) {
    if (authStore.authToken && to.name === 'Login') {
      // propage le deep-link (programId, tab, utm_*) vers /redirect, sans token
      const preservedQuery = { ...to.query }
      delete preservedQuery.token
      delete preservedQuery.from
      return next({ name: 'Redirect', query: preservedQuery })
    }
  }
  // 404
  if (!to.matched.length) {
    if (authStore.authToken) {
      return next({ name: 'Redirect' })
    } else {
      return next({ name: 'Login' })
    }
  }

  next()
})

// send pageview to posthog
router.afterEach((to) => {
  routeStore.setCurrentRoute(to)
  // WCAG 2.4.2 : le titre du document doit décrire la page atteinte. Dans une
  // SPA aucun rechargement ne le met à jour, il faut le faire à la navigation.
  setRouteTitleKey(to.meta?.titleKey)
  PosthogClient.capture('$pageview')
})

export default router
